import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useTranslation } from "@/hooks/useTranslation";
import { trpc } from "@/lib/trpc";
import { Settings2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

type IncomeSettingsDraft = {
  includeTraining: boolean;
  includeAddons: boolean;
  includedAddonCategories: string[];
  includeCustomAmount: boolean;
};

const defaultSettings: IncomeSettingsDraft = {
  includeTraining: true,
  includeAddons: true,
  includedAddonCategories: [],
  includeCustomAmount: true,
};

interface IncomePreferencesPanelProps {
  addonCategories: string[];
}

export function IncomePreferencesPanel({
  addonCategories,
}: IncomePreferencesPanelProps) {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  const { data: storedPreferences, isLoading } =
    trpc.trainerIncome.getPreferences.useQuery();
  const updatePreferences = trpc.trainerIncome.updatePreferences.useMutation({
    onSuccess: () => {
      void Promise.all([
        utils.trainerIncome.getPreferences.invalidate(),
        utils.dochadzka.getByDateRange.invalidate(),
      ]);
    },
  });
  const [settings, setSettings] =
    useState<IncomeSettingsDraft>(defaultSettings);
  const categoryKey = useMemo(
    () => addonCategories.join("\u0000"),
    [addonCategories]
  );

  useEffect(() => {
    const selectedCategories = storedPreferences
      ? storedPreferences.includedAddonCategories
      : addonCategories;

    setSettings({
      includeTraining: storedPreferences?.includeTraining ?? true,
      includeAddons: storedPreferences?.includeAddons ?? true,
      includedAddonCategories: selectedCategories,
      includeCustomAmount: storedPreferences?.includeCustomAmount ?? true,
    });
  }, [storedPreferences, categoryKey, addonCategories]);

  const toggleCategory = (category: string, checked: boolean) => {
    setSettings(current => ({
      ...current,
      includedAddonCategories: checked
        ? Array.from(new Set([...current.includedAddonCategories, category]))
        : current.includedAddonCategories.filter(item => item !== category),
    }));
  };

  const save = () => {
    updatePreferences.mutate(settings);
  };

  return (
    <Card className="mb-6">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Settings2 className="h-4 w-4" />
          {t("attendance:incomeSettings.title")}
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          {t("attendance:incomeSettings.description")}
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <p className="text-sm text-muted-foreground">{t("common:loading")}</p>
        ) : (
          <>
            <div className="flex flex-wrap gap-x-6 gap-y-3">
              <label className="flex items-center gap-2 text-sm font-medium">
                <Checkbox
                  checked={settings.includeTraining}
                  onCheckedChange={checked =>
                    setSettings(current => ({
                      ...current,
                      includeTraining: checked === true,
                    }))
                  }
                />
                {t("attendance:incomeSettings.training")}
              </label>
              <label className="flex items-center gap-2 text-sm font-medium">
                <Checkbox
                  checked={settings.includeAddons}
                  onCheckedChange={checked =>
                    setSettings(current => ({
                      ...current,
                      includeAddons: checked === true,
                    }))
                  }
                />
                {t("attendance:incomeSettings.addons")}
              </label>
              <label className="flex items-center gap-2 text-sm font-medium">
                <Checkbox
                  checked={settings.includeCustomAmount}
                  onCheckedChange={checked =>
                    setSettings(current => ({
                      ...current,
                      includeCustomAmount: checked === true,
                    }))
                  }
                />
                {t("attendance:incomeSettings.customAmount")}
              </label>
            </div>

            {settings.includeAddons && addonCategories.length > 0 && (
              <fieldset className="rounded-md border p-3">
                <legend className="px-1 text-sm font-medium">
                  {t("attendance:incomeSettings.addonCategories")}
                </legend>
                <div className="mt-2 flex flex-wrap gap-x-6 gap-y-3">
                  {addonCategories.map(category => {
                    const inputId = `income-category-${category}`;
                    const isIncluded =
                      settings.includedAddonCategories.includes(category);
                    return (
                      <div key={category} className="flex items-center gap-2">
                        <Checkbox
                          id={inputId}
                          checked={isIncluded}
                          onCheckedChange={checked =>
                            toggleCategory(category, checked === true)
                          }
                        />
                        <Label htmlFor={inputId}>{category}</Label>
                      </div>
                    );
                  })}
                </div>
              </fieldset>
            )}

            <Button
              type="button"
              size="sm"
              onClick={save}
              disabled={updatePreferences.isPending}
            >
              {updatePreferences.isPending
                ? t("common:loading")
                : t("common:save")}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
